import React, { useState, useEffect, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

import { 
    Package, RefreshCw, AlertCircle, Tag, DollarSign, 
    Activity, X, Save, Image as ImageIcon, CheckCircle,
    Percent, FileText, ToggleLeft, ToggleRight, Eye, EyeOff,
    LayoutGrid, List, Lock, Search, ShoppingCart, Plus, Minus, Trash2
} from 'lucide-react';
import { productoService } from '../../services/productoService';
import { useNavigate } from 'react-router-dom';

// ============================================================
// UTILIDADES: TOKEN Y PERMISOS
// ============================================================
const getUserType = () => {
    try {
        const token = localStorage.getItem("auth_token");
        if (!token) return null;
        const parts = token.split('.');
        if (parts.length >= 2) {
            const payload = JSON.parse(atob(parts[0]));
            return payload.tipo_user || payload.tipo_usuario || null;
        }
        return null;
    } catch (e) {
        return null;
    }
};


const isAdmin = () => getUserType() === 'admin';

// Componente de Imagen con Fallback
const ProductImage = ({ src, alt, isList }) => {
    const [imgError, setImgError] = useState(false);
    if (!src || imgError) {
        return (
            <div className={`relative bg-gray-100 flex flex-col items-center justify-center text-gray-400 ${isList ? 'w-24 h-24 sm:w-32 sm:h-32 shrink-0' : 'h-40 sm:h-44'}`}>
                <Package className={`${isList ? 'w-8 h-8 sm:w-10 sm:h-10' : 'w-10 h-10 sm:w-12 sm:h-12'} mb-1 sm:mb-2 opacity-40`} />
                <span className="text-[10px] sm:text-xs font-medium opacity-60">Sin imagen</span>
            </div>
        );
    }
    return (
        <div className={`relative bg-gray-50 overflow-hidden ${isList ? 'w-24 h-24 sm:w-32 sm:h-32 shrink-0' : 'h-40 sm:h-44'}`}>
            <img 
                src={src} alt={alt}
                className={`w-full h-full object-contain p-2 sm:p-4 transition-transform duration-500 ${!isList ? 'group-hover:scale-105' : ''}`}
                onError={() => setImgError(true)}
            />
        </div>
    );
};

const Productos = () => {
    const [productos, setProductos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [userIsAdmin, setUserIsAdmin] = useState(false);
    const navigate = useNavigate();

    // --- BÚSQUEDA Y VISTA ---
    const [searchQuery, setSearchQuery] = useState('');
    const [isGridView, setIsGridView] = useState(() => {
        return localStorage.getItem('product_view_mode') !== 'list';
    });

    // --- CARRITO DE COMPRAS ---
    const [cart, setCart] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);
    const [quantities, setQuantities] = useState({});

    // --- EDICIÓN ---
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentProduct, setCurrentProduct] = useState(null);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });

    const initialLoad = useRef(false);
    
    useEffect(() => { setUserIsAdmin(isAdmin()); }, []);

    const cargarProductos = async () => {
        setLoading(true);
        setError('');
        const result = await productoService.obtenerProductos();
        if (result.success) {
            setProductos(result.data);
        } else {
            setError(result.error);
            if (result.code === 401 || result.code === 403) setTimeout(() => navigate('/login'), 2000);
        }
        setLoading(false);
    };

    useEffect(() => {
    if (initialLoad.current) return; // ← Si ya se cargó, salir
    initialLoad.current = true;      // ← Marcar como cargado
    cargarProductos();
}, []);
    useEffect(() => { localStorage.setItem('product_view_mode', isGridView ? 'grid' : 'list'); }, [isGridView]);

    // --- LÓGICA DEL CARRITO ---
    const incrementQty = (id) => {
        setQuantities(prev => ({ ...prev, [id]: (prev[id] || 1) + 1 }));
    };

    const decrementQty = (id) => {
        setQuantities(prev => ({ ...prev, [id]: Math.max(1, (prev[id] || 1) - 1) }));
    };

const addToCart = (producto) => {
    const qty = quantities[producto.id_producto] || 1;
    const precioUnitario = parseFloat(producto.p_distribuidor) || 0;
    const svUnitario = parseFloat(producto.sv) || 0;
    const pvUnitario = parseFloat(producto.pv) || 0;

    setCart(prevCart => {
        const existing = prevCart.find(item => item.id_producto === producto.id_producto);
        if (existing) {
            return prevCart.map(item => 
                item.id_producto === producto.id_producto ? { ...item, cantidad: item.cantidad + qty } : item
            );
        }
        return [...prevCart, {
            id_producto: producto.id_producto,
            codigoproducto: producto.codigoproducto, // <-- AGREGADO
            nombreproducto: producto.nombreproducto,
            cantidad: qty,
            precioUnitario,
            svUnitario,
            pvUnitario,
            foto: producto.foto
        }];
    });
    setQuantities(prev => ({ ...prev, [producto.id_producto]: 1 }));
};


const generarPDF = () => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleString('es-PE');
    const orderNumber = Math.floor(100000 + Math.random() * 900000);

    // --- ENCABEZADO ---
    doc.setFontSize(22);
    doc.setTextColor(37, 99, 235); // Azul DXN
    doc.text("ORDEN DE COMPRA DXN", 14, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Fecha: ${currentDate}`, 14, 30);
    doc.text(`N° Orden: #${orderNumber}`, 14, 35);

    // --- TABLA DE PRODUCTOS ---
    const tableColumn = ["Código", "Producto", "Cant.", "P. Unit (S/)", "Total (S/)", "SV", "PV"];
    const tableRows = [];

    cart.forEach(item => {
        tableRows.push([
            item.codigoproducto || item.id_producto,
            item.nombreproducto,
            item.cantidad,
            item.precioUnitario.toFixed(2),
            (item.precioUnitario * item.cantidad).toFixed(2),
            (item.svUnitario * item.cantidad).toFixed(1),
            (item.pvUnitario * item.cantidad).toFixed(1)
        ]);
    });

    autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 45,
        theme: 'grid',
        headStyles: { 
            fillColor: [37, 99, 235], // Azul
            textColor: 255, 
            fontStyle: 'bold',
            halign: 'center'
        },
        styles: { fontSize: 9, cellPadding: 3 },
        columnStyles: {
            0: { cellWidth: 25, halign: 'center' },
            1: { cellWidth: 'auto' },
            2: { cellWidth: 15, halign: 'center' },
            3: { cellWidth: 25, halign: 'right' },
            4: { cellWidth: 25, halign: 'right' },
            5: { cellWidth: 20, halign: 'right' },
            6: { cellWidth: 20, halign: 'right' },
        }
    });

    // --- TOTALES ---
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    
    doc.text(`Total Productos: ${cartTotals.totalItems}`, 140, finalY, { align: 'right' });
    doc.text(`Total SV: ${cartTotals.totalSV.toFixed(2)}`, 140, finalY + 6, { align: 'right' });
    doc.text(`Total PV: ${cartTotals.totalPV.toFixed(2)}`, 140, finalY + 12, { align: 'right' });
    
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.setTextColor(37, 99, 235);
    doc.text(`TOTAL A PAGAR: S/ ${cartTotals.totalPrice.toFixed(2)}`, 140, finalY + 24, { align: 'right' });

    // --- PIE DE PÁGINA ---
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.setFont(undefined, 'normal');
    doc.text("Gracias por su compra en DXN - Documento generado automáticamente", 105, 280, { align: 'center' });

    // Descargar
    doc.save(`Orden_Compra_DXN_${orderNumber}.pdf`);
};



    const updateCartQty = (id_producto, newQty) => {
        if (newQty < 1) {
            setCart(prev => prev.filter(item => item.id_producto !== id_producto));
            return;
        }
        setCart(prev => prev.map(item => item.id_producto === id_producto ? { ...item, cantidad: newQty } : item));
    };

    const removeFromCart = (id_producto) => {
        setCart(prev => prev.filter(item => item.id_producto !== id_producto));
    };

    const cartTotals = useMemo(() => {
        return cart.reduce((acc, item) => {
            acc.totalItems += item.cantidad;
            acc.totalPrice += item.precioUnitario * item.cantidad;
            acc.totalSV += item.svUnitario * item.cantidad;
            acc.totalPV += item.pvUnitario * item.cantidad;
            return acc;
        }, { totalItems: 0, totalPrice: 0, totalSV: 0, totalPV: 0 });
    }, [cart]);

    const formatCurrency = (value) => new Intl.NumberFormat('es-PE', { style: 'currency', currency: 'PEN' }).format(parseFloat(value) || 0);

    const getPrecioConDescuento = (producto) => {
        if (producto.estado_descuento === 1 && producto.porcentajedescuento > 0) {
            return (parseFloat(producto.p_menudeo) || 0) * (1 - (parseFloat(producto.porcentajedescuento) / 100));
        }
        return null;
    };

    // --- FILTRADO ---
    const filteredProductos = useMemo(() => {
        if (!searchQuery.trim()) return productos;
        const q = searchQuery.toLowerCase().trim();
        return productos.filter(p => 
            (p.nombreproducto || '').toLowerCase().includes(q) || 
            (p.codigoproducto || '').toLowerCase().includes(q)
        );
    }, [productos, searchQuery]);

    // --- EDICIÓN ---
    const handleOpenEdit = (producto) => {
        if (!userIsAdmin) return;
        setCurrentProduct(producto);
        setFormData({
            nombreproducto: producto.nombreproducto,
            codigoproducto: producto.codigoproducto,
            p_distribuidor: parseFloat(producto.p_distribuidor) || 0,
            p_menudeo: parseFloat(producto.p_menudeo) || 0,
            sv: parseFloat(producto.sv) || 0,
            pv: parseFloat(producto.pv) || 0,
            estado_activo: producto.estado_activo,
            estado_descuento: producto.estado_descuento || 0,
            porcentajedescuento: parseFloat(producto.porcentajedescuento) || 0,
            descripciondescuento: producto.descripciondescuento || '',
            foto: producto.foto || ''
        });
        setSaveMessage({ type: '', text: '' });
        setIsEditModalOpen(true);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? (checked ? 1 : 0) : value }));
    };

    const handleSave = async (e) => {
        if (e) e.preventDefault();
        if (!userIsAdmin) return;
        setSaving(true);
        setSaveMessage({ type: '', text: '' });

        const camposAEnviar = {};
        Object.keys(formData).forEach(key => {
            const formValue = formData[key];
            const currentValue = currentProduct[key];
            let esDiferente = false;
            if (formValue === null && currentValue === null) esDiferente = false;
            else if (formValue === null || currentValue === null) esDiferente = true;
            else if (typeof formValue === 'number' || typeof currentValue === 'number') esDiferente = parseFloat(formValue) !== parseFloat(currentValue);
            else esDiferente = String(formValue) !== String(currentValue);
            
            if (esDiferente) camposAEnviar[key] = formValue;
        });

        if (camposAEnviar.estado_activo !== undefined) camposAEnviar.estado_activo = parseInt(camposAEnviar.estado_activo);
        if (camposAEnviar.estado_descuento !== undefined) camposAEnviar.estado_descuento = parseInt(camposAEnviar.estado_descuento);
        if (camposAEnviar.estado_descuento === 0) {
            camposAEnviar.porcentajedescuento = null;
            camposAEnviar.descripciondescuento = null;
        } else if (camposAEnviar.porcentajedescuento !== undefined && camposAEnviar.porcentajedescuento !== null) {
            camposAEnviar.porcentajedescuento = parseFloat(camposAEnviar.porcentajedescuento);
        }

        if (Object.keys(camposAEnviar).length === 0) {
            setSaveMessage({ type: 'info', text: 'No se realizaron cambios.' });
            setSaving(false);
            return;
        }

        try {
            const result = await productoService.editarProducto(currentProduct.id_producto, camposAEnviar);
            if (result.success) {
                setSaveMessage({ type: 'success', text: result.mensaje });
                setTimeout(() => { setIsEditModalOpen(false); cargarProductos(); }, 1500);
            } else {
                setSaveMessage({ type: 'error', text: result.error });
                if (result.code === 401 || result.code === 403) setTimeout(() => navigate('/login'), 2000);
            }
        } catch (error) {
            setSaveMessage({ type: 'error', text: 'Error inesperado al guardar.' });
        }
        setSaving(false);
    };

    const containerVariants = { hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.05 } } };
    const itemVariants = { hidden: { y: 20, opacity: 0 }, visible: { y: 0, opacity: 1, transition: { duration: 0.3 } } };

    // Componente de controles de cantidad para las tarjetas
    const QuantityControl = ({ producto }) => (
        <div className="flex items-center gap-2">
            <div className="flex items-center bg-gray-100 rounded-lg p-0.5 sm:p-1 border border-gray-200">
                <button 
                    onClick={(e) => { e.stopPropagation(); decrementQty(producto.id_producto); }} 
                    className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-white rounded shadow-sm text-gray-600 hover:text-blue-600 active:bg-gray-50 transition-colors"
                >
                    <Minus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
                <span className="w-8 sm:w-10 text-center text-sm font-semibold text-gray-900">{quantities[producto.id_producto] || 1}</span>
                <button 
                    onClick={(e) => { e.stopPropagation(); incrementQty(producto.id_producto); }} 
                    className="w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center bg-white rounded shadow-sm text-gray-600 hover:text-blue-600 active:bg-gray-50 transition-colors"
                >
                    <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
            </div>
            <button 
                onClick={(e) => { e.stopPropagation(); addToCart(producto); }}
                className="flex-1 sm:flex-none py-2 sm:py-1.5 px-3 sm:px-4 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-xs sm:text-sm font-bold rounded-lg transition-colors flex items-center justify-center gap-1.5 shadow-sm hover:shadow-md active:scale-95"
            >
                <ShoppingCart className="w-4 h-4" /> 
                <span className="hidden sm:inline">Agregar</span>
                <span className="sm:hidden">Agg</span>
            </button>
        </div>
    );

    return (
        <div className="p-3 sm:p-4 lg:p-6 w-full relative min-h-screen bg-gray-50">
            {/* === ENCABEZADO === */}

<motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-4 sm:mb-6">
    {/* Título */}
    <div className="flex flex-col gap-1 sm:gap-3 mb-3 sm:mb-4">
        <div>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 flex items-center gap-2 sm:gap-3">
                <Package className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-blue-600" /> 
                <span className="leading-tight">Catálogo de Productos</span>
            </h1>
            <p className="text-gray-500 mt-0.5 sm:mt-1 text-xs sm:text-sm lg:text-base">Gestión y visualización de productos DXN disponibles.</p>
        </div>
    </div>

    {!loading && !error && productos.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {/* FILA ÚNICA EN DESKTOP - Apilado en móvil */}
            <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
                
                {/* Buscador - Ocupa todo el ancho en móvil, flexible en desktop */}
                <div className="relative flex-1 min-w-0">
                    <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
                    <input 
                        type="text" 
                        placeholder="Buscar por nombre o código..." 
                        value={searchQuery} 
                        onChange={(e) => setSearchQuery(e.target.value)} 
                        className="w-full pl-10 pr-10 py-3 sm:py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all shadow-sm" 
                    />
                    {searchQuery && (
                        <button 
                            onClick={() => setSearchQuery('')} 
                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 sm:p-0.5 hover:bg-gray-100 rounded-full"
                        >
                            <X className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-gray-400" />
                        </button>
                    )}
                </div>

                {/* Controles - En una fila en desktop */}
                <div className="flex items-center gap-2 lg:gap-3 shrink-0">
                    {/* Toggle Grid/Lista */}
                    <div className="flex items-center bg-white p-1 rounded-lg border border-gray-200 shadow-sm">
                        <button 
                            onClick={() => setIsGridView(true)} 
                            className={`p-2 rounded-md transition-all ${isGridView ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
                            title="Vista Cuadrícula"
                        >
                            <LayoutGrid className="w-5 h-5" />
                        </button>
                        <button 
                            onClick={() => setIsGridView(false)} 
                            className={`p-2 rounded-md transition-all ${!isGridView ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-50'}`}
                            title="Vista Lista"
                        >
                            <List className="w-5 h-5" />
                        </button>
                    </div>

                    {/* BOTÓN DEL CARRITO CON CONTADOR */}
                    <button 
                        onClick={() => setIsCartOpen(true)} 
                        className="relative p-2.5 sm:p-2 bg-white border border-gray-200 rounded-xl text-gray-600 hover:text-blue-600 hover:border-blue-200 active:bg-blue-50 transition-all shadow-sm active:scale-95"
                        title="Ver carrito"
                    >
                        <ShoppingCart className="w-5 h-5 sm:w-4 sm:h-4" />
                        {cartTotals.totalItems > 0 && (
                            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white animate-in zoom-in duration-200">
                                {cartTotals.totalItems}
                            </span>
                        )}
                    </button>

                    {/* Botón Actualizar */}
                    <motion.button 
                        whileTap={{ scale: 0.95 }} 
                        onClick={cargarProductos} 
                        disabled={loading} 
                        className="flex items-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-xl shadow-md hover:bg-blue-700 active:bg-blue-800 active:scale-95 transition-all disabled:opacity-50 font-medium text-sm whitespace-nowrap"
                    >
                        <RefreshCw className={`w-4 h-4 sm:w-5 sm:h-5 ${loading ? 'animate-spin' : ''}`} />
                        <span className="hidden sm:inline">{loading ? '...' : 'Actualizar'}</span>
                        <span className="sm:hidden">Act.</span>
                    </motion.button>
                </div>
            </div>
        </motion.div>
    )}
</motion.div>

            {error && (
                <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <div><h3 className="font-semibold text-red-800 text-sm sm:text-base">Error al cargar</h3><p className="text-red-600 text-xs sm:text-sm">{error}</p></div>
                </motion.div>
            )}

            {loading && (
                <div className={isGridView ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-5" : "flex flex-col gap-3"}>
                    {[...Array(isGridView ? 6 : 4)].map((_, i) => (
                        <div key={i} className={`bg-white rounded-xl shadow-sm border border-gray-100 animate-pulse ${isGridView ? 'p-3 sm:p-4' : 'p-3 sm:p-4 flex gap-3 sm:gap-4'}`}>
                            <div className={`bg-gray-200 rounded-lg ${isGridView ? 'h-40 sm:h-44 w-full mb-3 sm:mb-4' : 'h-24 w-24 sm:w-32 shrink-0'}`}></div>
                            {isGridView ? (
                                <><div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div><div className="h-8 bg-gray-200 rounded w-full mt-auto"></div></>
                            ) : (
                                <div className="flex-1 space-y-2 sm:space-y-3 py-1"><div className="h-4 bg-gray-200 rounded w-1/2"></div><div className="h-8 bg-gray-200 rounded w-24 ml-auto"></div></div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {!loading && !error && (
                <>
                    {filteredProductos.length === 0 ? (
                        <div className="text-center py-16 sm:py-20 bg-white rounded-xl border border-dashed border-gray-300">
                            <Search className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mx-auto mb-3 sm:mb-4" />
                            <p className="text-gray-500 text-base sm:text-lg font-medium">No se encontraron productos</p>
                        </div>
                    ) : (
                        <motion.div variants={containerVariants} initial="hidden" animate="visible">
                            {/* ================= VISTA GRID (Móvil: 1 col, Tablet: 2, Desktop: 3-4) ================= */}
                            {isGridView && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
                                    {filteredProductos.map((producto) => {
                                        const precioDescuento = getPrecioConDescuento(producto);
                                        return (
                                            <motion.div 
                                                key={producto.id_producto} 
                                                variants={itemVariants} 
                                                whileTap={{ scale: 0.98 }}
                                                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg active:shadow-md transition-all duration-300 flex flex-col group relative"
                                            >
                                                {producto.estado_descuento === 1 && producto.porcentajedescuento > 0 && (
                                                    <div className="absolute top-2 left-2 z-10">
                                                        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="px-2 py-1 bg-red-500 text-white text-[10px] sm:text-xs font-bold rounded-md shadow-lg flex items-center gap-1">
                                                            <Percent className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> -{producto.porcentajedescuento}%
                                                        </motion.div>
                                                    </div>
                                                )}
                                                <div className="absolute top-2 right-2 z-10">
                                                    <div className={`px-2 py-1 rounded-full text-[10px] font-bold shadow-sm backdrop-blur-sm ${producto.estado_activo === 1 ? 'bg-green-100/90 text-green-700 border border-green-200' : 'bg-gray-100/90 text-gray-600 border border-gray-200'}`}>
                                                        {producto.estado_activo === 1 ? 'Activo' : 'Inactivo'}
                                                    </div>
                                                </div>
                                                <ProductImage src={producto.foto} alt={producto.nombreproducto} isList={false} />
                                                <div className="p-3 sm:p-4 flex-1 flex flex-col">
                                                    <div className="mb-3 flex-1">
                                                        <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight line-clamp-2 mb-1" title={producto.nombreproducto}>{producto.nombreproducto}</h3>
                                                        <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1"><Tag className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {producto.codigoproducto}</p>
                                                    </div>
                                                    <div className="space-y-1.5 pt-3 border-t border-gray-100 mb-3">
                                                        <div className="flex justify-between items-center text-xs">
                                                            <span className="text-gray-500 text-[10px] sm:text-xs">Distribuidor</span>
                                                            <span className="font-semibold text-gray-700 text-xs sm:text-sm">{formatCurrency(producto.p_distribuidor)}</span>
                                                        </div>
                                                        <div className="flex justify-between items-center text-xs">
                                                            <span className="text-gray-500 text-[10px] sm:text-xs">Menudeo</span>
                                                            <div className="flex flex-col items-end">
                                                                {precioDescuento ? (
                                                                    <>
                                                                        <span className="text-[10px] text-gray-400 line-through">{formatCurrency(producto.p_menudeo)}</span>
                                                                        <span className="font-bold text-red-600 text-sm">{formatCurrency(precioDescuento)}</span>
                                                                    </>
                                                                ) : (
                                                                    <span className="font-bold text-blue-600 text-sm">{formatCurrency(producto.p_menudeo)}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-1.5 mt-2">
                                                            <div className="text-center py-1.5 bg-purple-50 rounded border border-purple-100">
                                                                <p className="text-[9px] text-purple-600 font-bold uppercase">SV</p>
                                                                <p className="text-xs font-bold text-purple-800">{producto.sv}</p>
                                                            </div>
                                                            <div className="text-center py-1.5 bg-orange-50 rounded border border-orange-100">
                                                                <p className="text-[9px] text-orange-600 font-bold uppercase">PV</p>
                                                                <p className="text-xs font-bold text-orange-800">{producto.pv}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Controles de Cantidad y Agregar */}
                                                    <QuantityControl producto={producto} />
                                                    
                                                    {userIsAdmin && (
                                                        <button 
                                                            onClick={(e) => { e.stopPropagation(); handleOpenEdit(producto); }} 
                                                            className="w-full mt-2 py-2 text-gray-500 hover:text-blue-600 active:text-blue-700 text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
                                                        >
                                                            <Activity className="w-3.5 h-3.5" /> Editar Producto
                                                        </button>
                                                    )}
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* ================= VISTA LISTA (Optimizada para móvil) ================= */}
                            {!isGridView && (
                                <div className="flex flex-col gap-3">
                                    {filteredProductos.map((producto) => {
                                        const precioDescuento = getPrecioConDescuento(producto);
                                        return (
                                            <motion.div 
                                                key={producto.id_producto} 
                                                variants={itemVariants}
                                                whileTap={{ scale: 0.99 }}
                                                className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md active:shadow-sm transition-all duration-200 overflow-hidden group relative"
                                            >
                                                <div className="flex flex-col sm:flex-row">
                                                    <ProductImage src={producto.foto} alt={producto.nombreproducto} isList={true} />
                                                    
                                                    <div className="flex-1 p-3 sm:p-4">
                                                        <div className="flex items-start justify-between gap-2 mb-2">
                                                            <div className="min-w-0 flex-1">
                                                                <h3 className="font-bold text-gray-900 text-sm sm:text-base leading-tight line-clamp-2">{producto.nombreproducto}</h3>
                                                                <p className="text-[10px] sm:text-xs text-gray-500 flex items-center gap-1 mt-1"><Tag className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> {producto.codigoproducto}</p>
                                                            </div>
                                                            <div className="flex flex-col items-end gap-1 shrink-0">
                                                                {producto.estado_descuento === 1 && producto.porcentajedescuento > 0 && (
                                                                    <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-md shadow-sm">-{producto.porcentajedescuento}%</span>
                                                                )}
                                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${producto.estado_activo === 1 ? 'bg-green-50 text-green-700 border-green-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                                                                    {producto.estado_activo === 1 ? 'Activo' : 'Inactivo'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Precios y Puntos - Responsive */}
                                                        <div className="flex flex-wrap items-center gap-3 sm:gap-4 mb-3">
                                                            <div className="flex-1 min-w-[120px]">
                                                                <p className="text-[10px] text-gray-500 uppercase font-semibold">Menudeo</p>
                                                                {precioDescuento ? (
                                                                    <div className="flex flex-col">
                                                                        <span className="text-[10px] text-gray-400 line-through">{formatCurrency(producto.p_menudeo)}</span>
                                                                        <span className="text-base sm:text-lg font-bold text-red-600">{formatCurrency(precioDescuento)}</span>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-base sm:text-lg font-bold text-blue-600">{formatCurrency(producto.p_menudeo)}</span>
                                                                )}
                                                                <p className="text-[10px] text-gray-400">Dist: {formatCurrency(producto.p_distribuidor)}</p>
                                                            </div>
                                                            
                                                            <div className="flex gap-1.5 sm:gap-2">
                                                                <div className="text-center px-2 py-1.5 bg-purple-50 rounded border border-purple-100 min-w-[50px]">
                                                                    <p className="text-[9px] text-purple-600 font-bold">SV</p>
                                                                    <p className="text-xs font-bold text-purple-800">{producto.sv}</p>
                                                                </div>
                                                                <div className="text-center px-2 py-1.5 bg-orange-50 rounded border border-orange-100 min-w-[50px]">
                                                                    <p className="text-[9px] text-orange-600 font-bold">PV</p>
                                                                    <p className="text-xs font-bold text-orange-800">{producto.pv}</p>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        {/* Controles y Botones - Apilados en móvil */}
                                                        <div className="flex flex-col sm:flex-row gap-2">
                                                            <QuantityControl producto={producto} />
                                                            
                                                            {userIsAdmin && (
                                                                <button 
                                                                    onClick={(e) => { e.stopPropagation(); handleOpenEdit(producto); }} 
                                                                    className="w-full sm:w-auto py-2 px-3 text-gray-500 hover:text-blue-600 active:text-blue-700 text-xs font-medium transition-colors flex items-center justify-center gap-1.5 border border-gray-200 rounded-lg hover:bg-gray-50"
                                                                >
                                                                    <Activity className="w-3.5 h-3.5" /> Editar
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        );
                                    })}
                                </div>
                            )}
                        </motion.div>
                    )}
                </>
            )}

            {/* ========================================== */}
            {/* MODAL LATERAL DEL CARRITO (DRAWER)       */}
            {/* ========================================== */}
            <AnimatePresence>
                {isCartOpen && (
                    <>
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50" onClick={() => setIsCartOpen(false)} />
                        <motion.div 
                            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            className="fixed right-0 top-0 h-full w-full max-w-sm sm:max-w-md bg-white shadow-2xl z-50 flex flex-col"
                        >
                            {/* Header del Carrito */}
                            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
                                <h2 className="text-base sm:text-lg font-bold text-gray-900 flex items-center gap-2">
                                    <ShoppingCart className="w-5 h-5 text-blue-600" />
                                    Carrito de Venta
                                </h2>
                                <button onClick={() => setIsCartOpen(false)} className="p-2 hover:bg-gray-200 rounded-full transition-colors">
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>

                            {/* Lista de Items */}
                            <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
                                {cart.length === 0 ? (
                                    <div className="text-center py-16 text-gray-500">
                                        <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-20" />
                                        <p className="font-medium text-sm sm:text-base">El carrito está vacío</p>
                                        <p className="text-xs sm:text-sm mt-1">Agrega productos desde el catálogo</p>
                                    </div>
                                ) : (
                                    cart.map((item) => (
                                        <div key={item.id_producto} className="flex gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                            <div className="w-16 h-16 bg-white rounded-lg overflow-hidden shrink-0 flex items-center justify-center p-1">
                                                {item.foto ? (
                                                    <img src={item.foto} alt={item.nombreproducto} className="w-full h-full object-contain" />
                                                ) : <Package className="w-6 h-6 text-gray-300" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm font-semibold text-gray-900 line-clamp-2">{item.nombreproducto}</h4>
                                                <p className="text-xs text-gray-500 mt-0.5">S/ {item.precioUnitario.toFixed(2)} c/u</p>
                                                
                                                <div className="flex items-center justify-between mt-2">
                                                    <div className="flex items-center bg-white rounded-lg border border-gray-200 p-0.5">
                                                        <button onClick={() => updateCartQty(item.id_producto, item.cantidad - 1)} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded text-gray-600"><Minus className="w-3.5 h-3.5" /></button>
                                                        <span className="w-8 text-center text-sm font-semibold">{item.cantidad}</span>
                                                        <button onClick={() => updateCartQty(item.id_producto, item.cantidad + 1)} className="w-7 h-7 flex items-center justify-center hover:bg-gray-100 rounded text-gray-600"><Plus className="w-3.5 h-3.5" /></button>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-sm font-bold text-blue-600">S/ {(item.precioUnitario * item.cantidad).toFixed(2)}</p>
                                                        <p className="text-[10px] text-gray-500">SV: {(item.svUnitario * item.cantidad).toFixed(1)} | PV: {(item.pvUnitario * item.cantidad).toFixed(1)}</p>
                                                    </div>
                                                </div>
                                            </div>
                                            <button onClick={() => removeFromCart(item.id_producto)} className="text-gray-400 hover:text-red-600 self-start p-1 hover:bg-red-50 rounded transition-colors">
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))
                                )}
                            </div>

                            {/* Footer / Totales */}
                            {cart.length > 0 && (
                                <div className="p-4 border-t border-gray-100 bg-gray-50 shrink-0 space-y-3">
                                    <div className="space-y-2 text-xs sm:text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Total Productos:</span>
                                            <span className="font-semibold">{cartTotals.totalItems}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Total SV:</span>
                                            <span className="font-semibold text-purple-700">{cartTotals.totalSV.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-600">Total PV:</span>
                                            <span className="font-semibold text-orange-700">{cartTotals.totalPV.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between text-base font-bold text-gray-900 pt-3 border-t border-gray-200">
                                            <span>Total a Pagar:</span>
                                            <span className="text-lg sm:text-xl text-blue-600">{formatCurrency(cartTotals.totalPrice)}</span>
                                        </div>
                                    </div>

<button 
    onClick={() => {
        generarPDF();           // 1. Genera y descarga el PDF
        setCart([]);            // 2. Limpia el carrito de compras
        setQuantities({});      // 3. Reinicia los contadores de cantidad a 1
        setIsCartOpen(false);   // 4. Cierra el modal del carrito automáticamente
    }}
    className="w-full py-3.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 active:bg-blue-800 active:scale-95 transition-all shadow-lg shadow-blue-500/20 flex items-center justify-center gap-2 text-sm sm:text-base"
>
    <CheckCircle className="w-5 h-5" /> Procesar Venta
</button>



                                </div>
                            )}
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

            {/* ========================================== */}
            {/* MODAL DE EDICIÓN                           */}
            {/* ========================================== */}
            <AnimatePresence>
                {isEditModalOpen && currentProduct && userIsAdmin && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4">
                        <motion.div initial={{ scale: 0.95, opacity: 0, y: 20 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 20 }} className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                            <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-white shrink-0">
                                <div>
                                    <h2 className="text-base sm:text-xl font-bold text-gray-900">Editar Producto</h2>
                                    <p className="text-xs sm:text-sm text-gray-500">ID: {currentProduct.id_producto} • Código: {currentProduct.codigoproducto}</p>
                                </div>
                                <button onClick={() => setIsEditModalOpen(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
                                    <X className="w-5 h-5 text-gray-500" />
                                </button>
                            </div>
                            <form onSubmit={handleSave} className="flex-1 overflow-y-auto flex flex-col">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-0 flex-1">
                                    <div className="lg:col-span-1 bg-gray-50 p-4 sm:p-6 border-r border-gray-100 flex flex-col">
                                        <h3 className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 sm:mb-4 flex items-center gap-2"><Eye className="w-4 h-4" /> Vista Previa</h3>
                                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-3 sm:mb-4 aspect-square flex items-center justify-center p-3 sm:p-4">
                                            <ProductImage src={formData.foto} alt="Preview" isList={false} />
                                        </div>
                                        <div className="mb-3 sm:mb-4">
                                            <label className="block text-xs font-medium text-gray-600 mb-1">URL de la Imagen</label>
                                            <div className="relative">
                                                <ImageIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                                <input type="url" name="foto" value={formData.foto} onChange={handleInputChange} placeholder="https://..." className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none" />
                                            </div>
                                        </div>
                                        <div className={`p-3 sm:p-4 rounded-xl border-2 transition-all mt-auto ${formData.estado_activo === 1 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs sm:text-sm font-semibold text-gray-700">Estado del Producto</span>
                                                {formData.estado_activo === 1 ? <Eye className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" /> : <EyeOff className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />}
                                            </div>
                                            <button type="button" onClick={() => setFormData(prev => ({ ...prev, estado_activo: prev.estado_activo === 1 ? 0 : 1 }))} className="w-full flex items-center justify-between p-2 bg-white rounded-lg border border-gray-200 hover:shadow-sm transition-all">
                                                <span className={`text-xs font-bold ${formData.estado_activo === 1 ? 'text-green-700' : 'text-red-700'}`}>{formData.estado_activo === 1 ? 'ACTIVO' : 'INACTIVO'}</span>
                                                {formData.estado_activo === 1 ? <ToggleRight className="w-6 h-6 sm:w-8 sm:h-8 text-green-600" /> : <ToggleLeft className="w-6 h-6 sm:w-8 sm:h-8 text-red-500" />}
                                            </button>
                                        </div>
                                    </div>
                                    <div className="lg:col-span-2 p-4 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto">
                                        {saveMessage.text && (
                                            <div className={`p-3 rounded-lg flex items-center gap-2 ${saveMessage.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : saveMessage.type === 'error' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                                                {saveMessage.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                                                <span className="text-sm font-medium">{saveMessage.text}</span>
                                            </div>
                                        )}
                                        <div>
                                            <h3 className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 pb-2 border-b border-gray-100">Información Básica</h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                <div className="sm:col-span-2">
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">Nombre del Producto</label>
                                                    <input type="text" name="nombreproducto" value={formData.nombreproducto} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">Código</label>
                                                    <input type="text" name="codigoproducto" value={formData.codigoproducto} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 pb-2 border-b border-gray-100 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Precios</h3>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">Precio Distribuidor (S/)</label>
                                                    <input type="number" step="0.01" name="p_distribuidor" value={formData.p_distribuidor} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-xs font-medium text-gray-600 mb-1">Precio Menudeo (S/)</label>
                                                    <input type="number" step="0.01" name="p_menudeo" value={formData.p_menudeo} onChange={handleInputChange} required className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-sm" />
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 pb-2 border-b border-gray-100">Puntos y Volumen</h3>
                                            <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                                <div className="p-3 bg-purple-50 rounded-lg border border-purple-100">
                                                    <label className="block text-xs font-bold text-purple-700 mb-1 uppercase">Puntos SV</label>
                                                    <input type="number" step="0.1" name="sv" value={formData.sv} onChange={handleInputChange} required className="w-full px-3 py-2 bg-white border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none text-sm" />
                                                </div>
                                                <div className="p-3 bg-orange-50 rounded-lg border border-orange-100">
                                                    <label className="block text-xs font-bold text-orange-700 mb-1 uppercase">Puntos PV</label>
                                                    <input type="number" step="0.1" name="pv" value={formData.pv} onChange={handleInputChange} required className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-500 outline-none text-sm" />
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3 pb-2 border-b border-gray-100 flex items-center gap-2"><Percent className="w-4 h-4" /> Descuentos</h3>
                                            <div className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-100 mb-3">
                                                <div className="flex items-center gap-2 sm:gap-3">
                                                    {formData.estado_descuento === 1 ? <ToggleRight className="w-8 h-8 sm:w-10 sm:h-10 text-red-600" /> : <ToggleLeft className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />}
                                                    <div>
                                                        <p className="text-xs sm:text-sm font-semibold text-gray-800">Aplicar Descuento</p>
                                                        <p className="text-[10px] sm:text-xs text-gray-500">{formData.estado_descuento === 1 ? 'ACTIVO' : 'Inactivo'}</p>
                                                    </div>
                                                </div>
                                                <button type="button" onClick={() => setFormData(prev => ({ ...prev, estado_descuento: prev.estado_descuento === 1 ? 0 : 1 }))} className="px-2 sm:px-3 py-1 text-xs font-bold rounded-full border transition-colors bg-white hover:bg-gray-50">
                                                    {formData.estado_descuento === 1 ? 'No' : 'Sí'}
                                                </button>
                                            </div>
                                            <AnimatePresence>
                                                {formData.estado_descuento === 1 && (
                                                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                                                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 pt-2">
                                                            <div>
                                                                <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1"><Percent className="w-3 h-3" /> Porcentaje (%)</label>
                                                                <input type="number" min="0" max="100" step="0.1" name="porcentajedescuento" value={formData.porcentajedescuento} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm" placeholder="15" />
                                                            </div>
                                                            <div className="sm:col-span-2">
                                                                <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1"><FileText className="w-3 h-3" /> Descripción</label>
                                                                <input type="text" name="descripciondescuento" value={formData.descripciondescuento} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm" placeholder="Ej: Oferta especial" />
                                                            </div>
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex justify-between items-center px-4 sm:px-6 py-3 sm:py-4 border-t border-gray-100 bg-gray-50 shrink-0 gap-2 sm:gap-3">
                                    <div className="text-xs text-gray-500 hidden sm:block">
                                        {formData.estado_descuento === 1 && formData.porcentajedescuento > 0 && (
                                            <span className="px-2 py-1 bg-red-100 text-red-700 rounded font-semibold">
                                                Precio final: {formatCurrency((parseFloat(formData.p_menudeo) || 0) * (1 - (parseFloat(formData.porcentajedescuento) / 100)))}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex gap-2 sm:gap-3 w-full sm:w-auto">
                                        <button type="button" onClick={() => setIsEditModalOpen(false)} disabled={saving} className="flex-1 sm:flex-none px-4 sm:px-5 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 active:bg-gray-200 transition-colors disabled:opacity-50">
                                            Cancelar
                                        </button>
                                        <button type="submit" disabled={saving} className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-4 sm:px-6 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-md">
                                            {saving ? (
                                                <><RefreshCw className="w-4 h-4 animate-spin" /> Guardando...</>
                                            ) : (
                                                <><Save className="w-4 h-4" /> Guardar</>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </form>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};





export default Productos;